import Phaser from 'phaser';
import { ensureHeroTextures, HERO_COMBAT_TEXTURE_KEY } from './characterVisuals';
import {
  CHARACTER_CHOICES,
  PLAYABLE_CHARACTER_ID,
  type CharacterDefinition,
  type CharacterVisual,
} from './characters';
import { COLORS, VIEW_HEIGHT, VIEW_WIDTH } from './constants';

export class CharacterSelectScene extends Phaser.Scene {
  private starting = false;

  constructor() {
    super('character-select');
  }

  create(): void {
    this.starting = false;
    ensureHeroTextures(this);
    this.cameras.main.setBackgroundColor(COLORS.void);

    const backdrop = this.add.graphics();
    backdrop.fillGradientStyle(0x15110f, 0x15110f, 0x070a09, 0x070a09, 1);
    backdrop.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
    backdrop.fillStyle(0x4f2528, 0.12).fillCircle(VIEW_WIDTH / 2, 350, 245);
    backdrop.lineStyle(1, 0x8c7957, 0.09);
    for (let x = -VIEW_HEIGHT; x < VIEW_WIDTH; x += 48) {
      backdrop.lineBetween(x, 0, x + VIEW_HEIGHT, VIEW_HEIGHT);
    }

    this.add
      .text(VIEW_WIDTH / 2, 62, 'ВЫБЕРИ ГЕРОЯ', {
        fontFamily: 'Georgia, serif',
        fontSize: '29px',
        fontStyle: 'bold',
        color: '#eadbb3',
        stroke: '#17100d',
        strokeThickness: 5,
        letterSpacing: 2,
      })
      .setOrigin(0.5);
    this.add
      .text(VIEW_WIDTH / 2, 103, 'Каждый герой начинает забег со своим оружием', {
        fontSize: '12px',
        color: '#8f8674',
      })
      .setOrigin(0.5);

    const backButton = this.add
      .text(24, 28, '‹  НАЗАД', {
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#a99b7d',
      })
      .setInteractive({ useHandCursor: true });
    backButton.on('pointerover', () => backButton.setColor('#eadbb3'));
    backButton.on('pointerout', () => backButton.setColor('#a99b7d'));
    backButton.on('pointerdown', () => this.scene.start('menu'));

    const cardXs = [78, VIEW_WIDTH / 2, VIEW_WIDTH - 78];
    CHARACTER_CHOICES.forEach((character, index) => {
      this.createCharacterCard(character, cardXs[index]!);
    });

    this.add
      .text(VIEW_WIDTH / 2, 585, 'ИЗГНАННЫЙ РЫЦАРЬ', {
        fontFamily: 'Georgia, serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#e5d4aa',
      })
      .setOrigin(0.5);
    this.add
      .text(VIEW_WIDTH / 2, 616, 'ТЯЖЁЛЫЙ МЕЧ  •  БРОНЯ  •  БЛИЖНИЙ БОЙ', {
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#9c9079',
        letterSpacing: 1,
      })
      .setOrigin(0.5);

    const startButton = this.add
      .rectangle(VIEW_WIDTH / 2, 686, 326, 70, 0x52282d, 1)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.95)
      .setInteractive({ useHandCursor: true });
    const startLabel = this.add
      .text(VIEW_WIDTH / 2, 686, 'НАЧАТЬ ЗАБЕГ', {
        fontFamily: 'Georgia, serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#f0dfb4',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    const startRun = () => {
      if (this.starting) return;
      this.starting = true;
      startButton.disableInteractive().setFillStyle(0x352023, 1);
      startLabel.setText('ВОЙТИ ВО ТЬМУ');
      this.cameras.main.fadeOut(220, 6, 8, 7);
      this.time.delayedCall(230, () => this.scene.start('game', { characterId: PLAYABLE_CHARACTER_ID }));
    };
    startButton.on('pointerover', () => startButton.setFillStyle(0x6a3238, 1));
    startButton.on('pointerout', () => startButton.setFillStyle(0x52282d, 1));
    startButton.on('pointerdown', startRun);
    this.input.keyboard?.once('keydown-ENTER', startRun);
    this.input.keyboard?.once('keydown-SPACE', startRun);
    this.input.keyboard?.once('keydown-ESC', () => this.scene.start('menu'));

    this.add
      .text(VIEW_WIDTH / 2, VIEW_HEIGHT - 37, 'НОВЫЕ ГЕРОИ ПОЯВЯТСЯ ПОЗЖЕ', {
        fontSize: '10px',
        color: '#59564d',
        letterSpacing: 2,
      })
      .setOrigin(0.5);
    this.cameras.main.fadeIn(180, 6, 8, 7);
  }

  private createCharacterCard(character: CharacterDefinition, x: number): void {
    const available = character.status === 'available';
    const frameColor = available ? COLORS.parchmentDark : 0x555a56;
    const container = this.add.container(x, 344).setSize(126, 374);
    const shadow = this.add.rectangle(4, 7, 126, 374, 0x000000, 0.62);
    const frame = this.add
      .rectangle(0, 0, 126, 374, available ? 0x171512 : 0x111412, 1)
      .setStrokeStyle(available ? 3 : 2, frameColor, available ? 1 : 0.72);
    const inset = this.add
      .rectangle(0, 0, 116, 364, available ? 0x4f2528 : 0x333733, available ? 0.08 : 0.1)
      .setStrokeStyle(1, frameColor, 0.34);
    const topSeal = this.add
      .rectangle(0, -160, 98, 28, available ? 0x5a3032 : 0x343835, 1)
      .setStrokeStyle(1, frameColor, 0.72);
    const status = this.add
      .text(0, -160, available ? 'ДОСТУПЕН' : 'ЗАКРЫТ', {
        fontSize: '10px',
        fontStyle: 'bold',
        color: available ? '#eadbb3' : '#888d89',
        letterSpacing: 1,
      })
      .setOrigin(0.5);

    const portrait = available
      ? this.add.image(0, -24, HERO_COMBAT_TEXTURE_KEY).setScale(1.05)
      : this.createLockedSilhouette(character.visual);
    const name = this.add
      .text(0, 93, character.name, {
        fontFamily: 'Georgia, serif',
        fontSize: available ? '12px' : '11px',
        fontStyle: 'bold',
        color: available ? '#e1d2ad' : '#6f7470',
        align: 'center',
        wordWrap: { width: 108 },
      })
      .setOrigin(0.5);
    const role = this.add
      .text(0, 133, character.role, {
        fontSize: '9px',
        fontStyle: 'bold',
        color: available ? '#9e927a' : '#5d625e',
        align: 'center',
        wordWrap: { width: 106 },
      })
      .setOrigin(0.5);
    const footer = this.add
      .text(0, 168, available ? '✓ ВЫБРАН' : 'В РАЗРАБОТКЕ', {
        fontSize: available ? '10px' : '8px',
        fontStyle: 'bold',
        color: available ? '#e6c980' : '#707570',
        letterSpacing: available ? 1 : 0,
      })
      .setOrigin(0.5);

    container.add([shadow, frame, inset, topSeal, status, portrait, name, role, footer]);

    if (available) {
      container.setInteractive(
        new Phaser.Geom.Rectangle(-63, -187, 126, 374),
        Phaser.Geom.Rectangle.Contains,
      );
      if (container.input) container.input.cursor = 'pointer';
      container.on('pointerover', () => {
        frame.setFillStyle(0x2b211d, 1).setStrokeStyle(4, 0xd0bd8f, 1);
        container.setScale(1.018);
      });
      container.on('pointerout', () => {
        frame.setFillStyle(0x171512, 1).setStrokeStyle(3, frameColor, 1);
        container.setScale(1);
      });
    }
  }

  private createLockedSilhouette(visual: CharacterVisual): Phaser.GameObjects.Container {
    const silhouette = this.add.container(0, -29);
    const figure = this.add.graphics();
    figure.fillStyle(0x090b0a, 0.92).fillEllipse(0, 71, 70, 16);
    if (visual === 'sorceress') {
      figure.fillStyle(0x343936, 1).fillTriangle(-37, 62, 0, -30, 36, 62);
      figure.fillCircle(0, -36, 17);
      figure.fillStyle(0x4a4f4b, 1).fillTriangle(-34, -49, 17, -91, 29, -43);
      figure.fillRoundedRect(-31, -49, 64, 9, 4);
      figure.lineStyle(8, 0x3d423e, 1).lineBetween(-29, -5, -49, 57);
      figure.lineStyle(4, 0x737873, 0.72).lineBetween(-49, -70, -49, 60);
      figure.fillStyle(0x777c77, 0.76).fillCircle(-49, -74, 7);
    } else if (visual === 'skeleton') {
      figure.fillStyle(0x686d68, 1).fillCircle(0, -58, 20);
      figure.fillStyle(0x171a18, 1).fillCircle(-7, -62, 5).fillCircle(7, -62, 5);
      figure.fillTriangle(-4, -51, 4, -51, 0, -45);
      figure.lineStyle(7, 0x656a65, 1).lineBetween(0, -38, 0, 31);
      figure.lineStyle(5, 0x606560, 1);
      for (let rib = 0; rib < 4; rib += 1) {
        const ribY = -25 + rib * 12;
        figure.lineBetween(-20 + rib * 2, ribY, 20 - rib * 2, ribY);
      }
      figure.lineStyle(7, 0x5b605b, 1).lineBetween(-10, 28, -19, 65);
      figure.lineBetween(10, 28, 19, 65);
      figure.lineStyle(6, 0x5b605b, 1).lineBetween(-16, -21, -35, 25);
      figure.lineBetween(16, -21, 36, 24);
      figure.fillStyle(0x747974, 1).fillTriangle(-14, 23, 14, 23, 0, 39);
    }
    figure.setAlpha(0.72);

    const lockBody = this.add
      .rectangle(0, 15, 42, 35, 0x777d78, 0.96)
      .setStrokeStyle(2, 0xa1a5a0, 0.72);
    const lockShackle = this.add.graphics();
    lockShackle.lineStyle(7, 0x8d928d, 1);
    lockShackle.beginPath().arc(0, -4, 14, Math.PI, 0, false).strokePath();
    const keyhole = this.add.graphics();
    keyhole.fillStyle(0x252925, 1).fillCircle(0, 11, 4).fillTriangle(-3, 13, 3, 13, 0, 23);
    silhouette.add([figure, lockShackle, lockBody, keyhole]);
    return silhouette;
  }
}
